import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { DropDownTypes, ExportPPTViewType, IAllChartSummary, IDisplayState, IFilterData, IFlatChartsCache, IFlatChartsCacheItem, ITemplate } from "../models/interfaces";
import { colorPallate } from "../transformer";
import { IStudyData } from "src/pages/Studies/models/interfaces";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

interface ISegment {
	id?: string;
	name?: string;
	value?: string | number;
}

interface ISelectedChartInterface {
	segments?: ISegment[];
}

interface IChartPanelDropdownSelection {
	selectedItem?: string;
	selectedItemName?: string;
}

interface ISectionTotalQuestions {
	[question: string]: number;
}

interface IChart extends Record<string, unknown> {
	id?: string;
	name?: string;
	data?: unknown[];
}

interface ILoadedChart {
	section_name: string;
	charts: IChart[];
}

interface ISectionalSummaryState {
	sectionalSummary: IFilterState;
}

interface IFilterState {
	sectionalSummaryAvailable: boolean;
	selectedChart: boolean;
	sectionalSummary: any;
	loading: boolean;
	quantError: string | null | boolean;
	eroorMessage: string;
	errorCode: number;
	selectedSegments: string[];
	selectedSegments___: string[];
	allFiltersData: IFilterData[];
	isFilterFocused: boolean;
	selectedFilters: string[];
	analysisData: any[];
	selectedAnalysisChart: ISelectedChartInterface;
	chartPanel: any[];
	mainChartData: {};
	activeSegments: ISegment[];
	masterDropDown: any[];
	openFilter: boolean;
	searchtext: string;
	chartPanelDropdown: string;
	chartPanelDropdown2: string;
	chartPanelQuestion: string;
	filterArray: string[];
	pageNo: number;
	rowCount: number;
	AllFiters: any[];
	filterSearchtext: string;
	selectedChartInChartPanel: number;
	segmentMenuSelect: string | null;
	mainChartPanel: any;
	sectionalCategory: string[];
	stopScrolling: boolean;
	stopScrollingChartPanel: boolean;
	showChartAnalysis: boolean;
	chartPanelSelected: number;
	mainChartSCrolled: number;
	sectionalChartsData: Record<string, unknown> | null;
	selectedItem: number | string;
	defaultColorPalate: string[];
	sectionsTotalQuestions: ISectionTotalQuestions;
	chartPanelChart: Record<string, unknown>;
	allLoadedCharts: ILoadedChart[];
	isCompareActive: boolean;
	chartPanelDropdownSelections: Record<string, IChartPanelDropdownSelection>;
	tabsMapping: {
		executiveIndex: string | null;
		sectionalIndex: string | null;
	};
	chartDataPointSelections: { [chartId: string]: unknown };
	openDrawer: boolean;
	activeDrawerTabKey: string;
	sectionalChartsAISummary: Record<string, unknown>;
	displayState: IDisplayState;
	allChartSummary: IAllChartSummary[];
	openEndedDrawerTabActiveKey: { key: number; name: string };
	openEndedDrawer: boolean;
	currentChartIndex: number | null;
	sectionalDashboardModalOpen: boolean;
	isDataUpdateModalOpen: boolean;
	dataUpdateModalMessage: string;

	// Cache implementation
	allChartsCacheFlat: IFlatChartsCache;
	allChartsSummaryCacheFlat: IFlatChartsCache;
	selectedChartsForExportHash: {
		hashKey: string;
		is_tabular: boolean;
		payload: any;
	}[];
	selectedOEDataForExport: {
		[hashKey: string]: {
			[tabKey: string]: {
				tabName: string;
				data: { idx: unknown; resp_val: unknown }[];
			};
		};
	};
	chartTableViewNew: {
		tab_name: string;
		section_name: string;
		chart_name: string;
		page_no: number;
		dropdownConfig: { [key: string]: string | string[] };
		is_tabular?: boolean;
	}[];
	downloadSelectedTriggered: number;
	isFilterApplied: boolean;
	isFilterCleared: boolean;
	allTemplates: ITemplate[];
	selectedTemplate: ITemplate;
	exportPPTView: ExportPPTViewType;
	isTemplateUploading: boolean;
	searchMode: {
		isActive: boolean;
		selectedChart: any;
		originalSection: string;
	};
	searchObj: {
		searchText: string;
		selectedIndex: number;
		isSearchedFromMain: boolean;
	};
	chartsViewStatus: IChartViewStatus[];
}

export const initialState: IFilterState = {
	sectionalSummaryAvailable: false,
	selectedChart: false,
	sectionalSummary: [],
	loading: false,
	quantError: null,
	eroorMessage: "",
	errorCode: 404,
	selectedSegments: [], // split by
	selectedSegments___: [], // split by
	allFiltersData: [],
	isFilterFocused: false,
	selectedFilters: [],
	analysisData: [],
	selectedAnalysisChart: { segments: [] },
	chartPanel: [],
	mainChartData: {},
	activeSegments: [],
	masterDropDown: [],
	openFilter: false,
	searchtext: "",
	chartPanelDropdown: "",
	chartPanelDropdown2: "",
	chartPanelQuestion: "",
	filterArray: [], // side drawer
	pageNo: 1,
	rowCount: 500,
	AllFiters: [],
	filterSearchtext: "",
	selectedChartInChartPanel: 0,
	segmentMenuSelect: "",
	mainChartPanel: [],
	sectionalCategory: [],
	stopScrolling: false,
	stopScrollingChartPanel: false,
	showChartAnalysis: false,
	chartPanelSelected: 0,
	mainChartSCrolled: 0,
	sectionalChartsData: null,
	selectedItem: 0,
	defaultColorPalate: colorPallate,
	sectionsTotalQuestions: {},
	chartPanelChart: {},
	allLoadedCharts: [],
	isCompareActive: false,
	chartPanelDropdownSelections: {},
	tabsMapping: {
		executiveIndex: null,
		sectionalIndex: null,
	},
	chartDataPointSelections: {},
	openDrawer: false,
	activeDrawerTabKey: DropDownTypes.Filters,
	sectionalChartsAISummary: {},
	displayState: {
		visibleCount: 10, // Initially show 12 charts
		isLoadingMoreDisplay: false,
		totalPages: null,
	},
	allChartSummary: [],
	openEndedDrawerTabActiveKey: { key: 0, name: "" },
	openEndedDrawer: false,
	currentChartIndex: null,
	sectionalDashboardModalOpen: false,
	isDataUpdateModalOpen: false,
	dataUpdateModalMessage: "",

	allChartsCacheFlat: {},
	allChartsSummaryCacheFlat: {},
	selectedChartsForExportHash: [],
	selectedOEDataForExport: {},
	chartTableViewNew: [],
	downloadSelectedTriggered: 0,
	isFilterApplied: false,
	isFilterCleared: false,
	allTemplates: [],
	selectedTemplate: null,
	exportPPTView: ExportPPTViewType.Charts,
	isTemplateUploading: false,
	searchMode: {
		isActive: false,
		selectedChart: null,
		originalSection: null,
	},
	searchObj: {
		searchText: "",
		selectedIndex: -1,
		isSearchedFromMain: false,
	},
	chartsViewStatus: [],
};

const sectionalSummarySlice = createSlice({
	name: "sectionalSummaryData",
	initialState: {
		sectionalSummary: initialState,
	} as ISectionalSummaryState,
	reducers: {
		setSectionalSummaryAvailable: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.sectionalSummaryAvailable = action.payload;
		},
		toggleSelectedChart: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.selectedChart = Boolean(action.payload);
		},
		setSectionalSummaryData: (state, action: PayloadAction<Record<string, unknown>>) => {
			state.sectionalSummary.sectionalSummary = action.payload;
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.loading = Boolean(action.payload);
		},
		setQuantError: (state, action: PayloadAction<string | null | boolean>) => {
			state.sectionalSummary.quantError = action.payload;
		},
		setErrorMessage: (state, action: PayloadAction<string>) => {
			state.sectionalSummary.eroorMessage = action.payload;
		},
		setErrorCode: (state, action: PayloadAction<number>) => {
			state.sectionalSummary.errorCode = Number(action.payload);
		},
		setSelectedSegments: (state, action: PayloadAction<string[]>) => {
			state.sectionalSummary.selectedSegments = action.payload;
		},
		setSelectedSegments___: (state, action: PayloadAction<string[]>) => {
			state.sectionalSummary.selectedSegments___ = action.payload;
		},
		setAllFiltersData: (state, action: PayloadAction<IFilterData[]>) => {
			state.sectionalSummary.allFiltersData = action.payload;
		},
		setIsFilterFocused(state, action: PayloadAction<boolean>) {
			state.sectionalSummary.isFilterFocused = action.payload;
		},
		setSelectedFilters(state, action: PayloadAction<string[]>) {
			state.sectionalSummary.selectedFilters = action.payload;
		},
		setAnalysisiData(state, action: PayloadAction<Record<string, unknown>[]>) {
			state.sectionalSummary.analysisData = action.payload;
		},
		setChartDataPointSelections: (state, action: PayloadAction<{ chartId: string; selectedPoints: unknown }>) => {
			const { chartId, selectedPoints } = action.payload;
			state.sectionalSummary.chartDataPointSelections[chartId] = selectedPoints;
		},
		setSelectedAnalysisChart(state, action: PayloadAction<ISelectedChartInterface>) {
			state.sectionalSummary.selectedAnalysisChart = action.payload;
		},
		setMainChart(state, action: PayloadAction<Record<string, unknown>>) {
			state.sectionalSummary.mainChartData = action.payload;
		},
		setActiveSegments(state, action: PayloadAction<ISegment[]>) {
			state.sectionalSummary.activeSegments = action.payload;
		},
		setMasterDropDown(state, action: PayloadAction<Record<string, unknown>[]>) {
			state.sectionalSummary.masterDropDown = action.payload;
		},
		filterSegmentDrawer: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.openFilter = Boolean(action.payload);
		},
		setSearchText(state, action: PayloadAction<string>) {
			state.sectionalSummary.searchtext = action.payload;
		},
		setChartPanelDropDown(state, action: PayloadAction<string>) {
			state.sectionalSummary.chartPanelDropdown = action.payload;
			state.sectionalSummary.analysisData = [];
		},
		setChartPanelDropDown2(state, action: PayloadAction<string>) {
			state.sectionalSummary.chartPanelDropdown2 = action.payload;
			state.sectionalSummary.analysisData = [];
		},
		setChartPanelQuestion(state, action: PayloadAction<string>) {
			state.sectionalSummary.chartPanelQuestion = action.payload;
		},
		setFilterArray(state, action: PayloadAction<string[]>) {
			state.sectionalSummary.filterArray = action.payload;
		},
		setPaneNo: (state, action: PayloadAction<number>) => {
			state.sectionalSummary.pageNo = Number(action.payload);
		},
		setRowCount: (state, action: PayloadAction<number>) => {
			state.sectionalSummary.rowCount = Number(action.payload);
		},
		setAllFilter(state, action: PayloadAction<Record<string, unknown>[]>) {
			state.sectionalSummary.AllFiters = action.payload;
		},
		setFilterSearchText(state, action: PayloadAction<string>) {
			state.sectionalSummary.filterSearchtext = action.payload;
		},
		setSelectedChartInChartPanel: (state, action: PayloadAction<number>) => {
			state.sectionalSummary.selectedChartInChartPanel = Number(action.payload);
		},
		setSegmentMenuSelect: (state, action: PayloadAction<string | null>) => {
			state.sectionalSummary.segmentMenuSelect = action.payload;
		},
		setSectionalCategory(state, action: PayloadAction<string[]>) {
			state.sectionalSummary.sectionalCategory = action.payload;
		},
		setStopScrolling: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.stopScrolling = Boolean(action.payload);
		},
		setStopScrollingChartPanel: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.stopScrollingChartPanel = Boolean(action.payload);
		},
		setShowChartAnalysis: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.showChartAnalysis = Boolean(action.payload);
		},
		setSectionalChartsData: (state, action: PayloadAction<Record<string, unknown> | null>) => {
			state.sectionalSummary.sectionalChartsData = action.payload;
		},
		setSelectedItem: (state, action: PayloadAction<number | string>) => {
			state.sectionalSummary.selectedItem = action.payload;
		},
		setChartPanelSelected: (state, action: PayloadAction<number>) => {
			state.sectionalSummary.chartPanelSelected = Number(action.payload);
		},
		setMainChartSCrolled: (state, action: PayloadAction<number>) => {
			state.sectionalSummary.mainChartSCrolled = Number(action.payload);
		},
		setSectionsTotalQuestions(state, action: PayloadAction<{ question: string; totalQuestions: number }>) {
			const { question, totalQuestions } = action.payload;
			state.sectionalSummary.sectionsTotalQuestions[question] = totalQuestions;
		},
		setChartPanelChart: (state, action: PayloadAction<Record<string, unknown>>) => {
			state.sectionalSummary.chartPanelChart = action.payload;
		},
		resetSectionTotalQuestions(state) {
			state.sectionalSummary.sectionsTotalQuestions = {};
		},
		setAllLoadedCharts(state, action: PayloadAction<{ section_name: string; charts: IChart | IChart[]; reset: boolean }>) {
			const { section_name, charts, reset } = action.payload;
			const existingSectionIndex = state.sectionalSummary.allLoadedCharts.findIndex((section) => section.section_name === section_name);

			if (reset) {
				// If resetSection is true, clear the charts for the section
				if (existingSectionIndex !== -1) {
					state.sectionalSummary.allLoadedCharts[existingSectionIndex].charts = Array.isArray(charts) ? charts : [charts];
				} else {
					state.sectionalSummary.allLoadedCharts.push({
						section_name,
						charts: Array.isArray(charts) ? charts : [charts],
					});
				}
			} else {
				// If resetSection is false, append the charts
				if (existingSectionIndex !== -1) {
					state.sectionalSummary.allLoadedCharts[existingSectionIndex].charts.push(...(Array.isArray(charts) ? charts : [charts]));
				} else {
					state.sectionalSummary.allLoadedCharts.push({
						section_name,
						charts: Array.isArray(charts) ? charts : [charts],
					});
				}
			}
		},
		filterLoadedChartsBySection(state, action: PayloadAction<{ section_name: string }>) {
			const { section_name } = action.payload;
			state.sectionalSummary.allLoadedCharts = state.sectionalSummary.allLoadedCharts.filter((section) => section.section_name !== section_name);
		},
		resetAllLoadedCharts(state) {
			state.sectionalSummary.allLoadedCharts = [];
		},
		setIsCompareActive(state, action: PayloadAction<boolean>) {
			state.sectionalSummary.isCompareActive = action.payload;
		},
		setChartPanelDropDownSelections(state, action: PayloadAction<{ chartIndex: string | number; selectedItem: string; selectedItemName: string }>) {
			const { chartIndex, selectedItem, selectedItemName } = action.payload;
			state.sectionalSummary.chartPanelDropdownSelections = {
				...state.sectionalSummary.chartPanelDropdownSelections,
				[chartIndex]: { selectedItem, selectedItemName },
			};
		},
		resetChartPanelDropDownSelections(state, action: PayloadAction<Record<string, IChartPanelDropdownSelection>>) {
			state.sectionalSummary.chartPanelDropdownSelections = action.payload;
		},
		updateTabsMapping(state, action: PayloadAction<{ executiveIndex: string | null; sectionalIndex: string | null }>) {
			const { executiveIndex, sectionalIndex } = action.payload;
			state.sectionalSummary.tabsMapping.executiveIndex = executiveIndex;
			state.sectionalSummary.tabsMapping.sectionalIndex = sectionalIndex;
		},
		setOpenDrawer: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.openDrawer = action.payload;
		},
		setActiveDrawerTabKey: (state, action: PayloadAction<string>) => {
			state.sectionalSummary.activeDrawerTabKey = action.payload;
		},
		setSectionalChartsAISummary: (state, action: PayloadAction<Record<string, unknown>>) => {
			state.sectionalSummary.sectionalChartsAISummary = action.payload;
		},
		setDisplayState: (state, action: PayloadAction<IDisplayState>) => {
			state.sectionalSummary.displayState = action.payload;
		},
		setIsDataUpdateModalOpen(state, action: PayloadAction<boolean>) {
			state.sectionalSummary.isDataUpdateModalOpen = action.payload;
		},
		setDataUpdateModalMessage(state, action: PayloadAction<string>) {
			state.sectionalSummary.dataUpdateModalMessage = action.payload;
		},
		updateChartTableViewNew(
			state,
			action: PayloadAction<{
				tab_name: string;
				section_name: string;
				chart_name: string;
				page_no: number;
				dropdownConfig: { [key: string]: string | string[] };
				is_tabular?: boolean;
			}>
		) {
			const { tab_name, section_name, chart_name, page_no, dropdownConfig, is_tabular } = action.payload;
			// Ensure chartTableViewNew is always an array
			if (!Array.isArray(state.sectionalSummary.chartTableViewNew)) {
				state.sectionalSummary.chartTableViewNew = [];
			}
			// Find index in chartTableViewNew
			const idx = state.sectionalSummary.chartTableViewNew.findIndex(
				(item) =>
					item.tab_name === tab_name &&
					item.section_name === section_name &&
					item.chart_name === chart_name &&
					item.page_no === page_no &&
					JSON.stringify(item.dropdownConfig) === JSON.stringify(dropdownConfig)
			);

			if (is_tabular) {
				// Add or update
				if (idx === -1) {
					state.sectionalSummary.chartTableViewNew.push({
						tab_name,
						section_name,
						chart_name,
						page_no,
						dropdownConfig,
						is_tabular: true,
					});
				} else {
					state.sectionalSummary.chartTableViewNew[idx].is_tabular = true;
				}
			} else {
				// Remove if exists
				if (idx !== -1) {
					state.sectionalSummary.chartTableViewNew.splice(idx, 1);
				}
			}
		},
		updateAllChartsCacheFlat(state, action: PayloadAction<{ hashKey: string; data: IFlatChartsCacheItem }>) {
			const { hashKey, data } = action.payload;
			state.sectionalSummary.allChartsCacheFlat[hashKey] = data;
		},
		updateAllChartsSummaryCacheFlat(state, action: PayloadAction<{ hashKey: string; data: IFlatChartsCacheItem }>) {
			const { hashKey, data } = action.payload;
			state.sectionalSummary.allChartsSummaryCacheFlat[hashKey] = data;
		},
		resetAllChartsCacheFlat(state) {
			state.sectionalSummary.allChartsCacheFlat = {};
		},
		resetAllChartsSummaryCacheFlat(state) {
			state.sectionalSummary.allChartsSummaryCacheFlat = {};
		},
		resetAllChartsCacheBasic(state, action: PayloadAction<IStudyData[]>) {
			const studiesData = action.payload;
			const filteredFlat: IFlatChartsCache = {};
			const now = dayjs().utc();
			Object.entries(state.sectionalSummary.allChartsCacheFlat).forEach(([hashKey, cacheItem]) => {
				const study = studiesData.find((s) => s.study_id === cacheItem.studyId);
				if (!study) {
					return;
				}
				const createdAt = cacheItem?.metadata?.createdAt ? dayjs(cacheItem.metadata.createdAt) : null;
				const isNotExpired = createdAt ? now.diff(createdAt, "day") <= 7 : true;
				// if (study?.version_history?.modified_dt && cacheItem?.lastUpdatedAt === study?.version_history?.modified_dt && isNotExpired) {
				// 	filteredFlat[hashKey] = cacheItem;
				// }
				if (study?.active_insights_id && cacheItem?.activeInsightsId === study?.active_insights_id && isNotExpired) {
					filteredFlat[hashKey] = cacheItem;
				}
			});
			state.sectionalSummary.allChartsCacheFlat = filteredFlat;
		},
		resetAllChartsSummaryCacheBasic(state, action: PayloadAction<IStudyData[]>) {
			const studiesData = action.payload;
			const filteredFlat: IFlatChartsCache = {};
			const now = dayjs().utc();
			Object.entries(state.sectionalSummary.allChartsSummaryCacheFlat).forEach(([hashKey, cacheItem]) => {
				const study = studiesData.find((s) => s.study_id === cacheItem.studyId);
				if (!study) {
					return;
				}
				const createdAt = cacheItem?.metadata?.createdAt ? dayjs(cacheItem.metadata.createdAt) : null;
				const isNotExpired = createdAt ? now.diff(createdAt, "day") <= 7 : true;
				// if (study?.version_history?.modified_dt && cacheItem?.lastUpdatedAt === study?.version_history?.modified_dt && isNotExpired) {
				// 	filteredFlat[hashKey] = cacheItem;
				// }
				if (study?.active_insights_id && cacheItem?.activeInsightsId === study?.active_insights_id && isNotExpired) {
					filteredFlat[hashKey] = cacheItem;
				}
			});
			state.sectionalSummary.allChartsSummaryCacheFlat = filteredFlat;
		},
		filterChartsCacheFlatByStudy(state, action: PayloadAction<string>) {
			const studyId = action.payload;
			const filteredFlat: IFlatChartsCache = {};
			Object.entries(state.sectionalSummary.allChartsCacheFlat).forEach(([hashKey, cacheItem]) => {
				if (cacheItem.studyId !== studyId) {
					filteredFlat[hashKey] = cacheItem;
				}
			});
			state.sectionalSummary.allChartsCacheFlat = filteredFlat;
		},
		filterChartsSummaryCacheFlatByStudy(state, action: PayloadAction<string>) {
			const studyId = action.payload;
			const filteredFlat: IFlatChartsCache = {};
			Object.entries(state.sectionalSummary.allChartsSummaryCacheFlat).forEach(([hashKey, cacheItem]) => {
				if (cacheItem.studyId !== studyId) {
					filteredFlat[hashKey] = cacheItem;
				}
			});
			state.sectionalSummary.allChartsSummaryCacheFlat = filteredFlat;
		},
		updateSelectedChartsForExportHash: (state, action: PayloadAction<{ hashKey: string; is_tabular: boolean; isAdded: boolean; payload: any }>) => {
			const { hashKey, is_tabular, isAdded, payload } = action.payload;
			if (isAdded) {
				const index = state.sectionalSummary.selectedChartsForExportHash.findIndex((item) => item.hashKey === hashKey);
				if (index !== -1) {
					state.sectionalSummary.selectedChartsForExportHash.splice(index, 1);
				}
				state.sectionalSummary.selectedChartsForExportHash.push({ hashKey, is_tabular, payload });
			} else {
				state.sectionalSummary.selectedChartsForExportHash = state.sectionalSummary.selectedChartsForExportHash.filter((item) => !(item.hashKey === hashKey));
			}
		},
		resetSelectedChartsForExportHash(state) {
			state.sectionalSummary.selectedChartsForExportHash = [];
		},
		setAllSummaryData(state, action: PayloadAction<IAllChartSummary[]>) {
			state.sectionalSummary.allChartSummary = action.payload;
		},
		setSingleSummaryData(state, action: PayloadAction<IAllChartSummary>) {
			const existingIndex = state.sectionalSummary.allChartSummary.findIndex((item) => item.pageNo === action.payload.pageNo);

			if (existingIndex !== -1) {
				state.sectionalSummary.allChartSummary[existingIndex] = action.payload;
			}
		},
		updateSummaryLoading(state, action: PayloadAction<{ pageNo: number; loading: boolean; isStale: boolean }>) {
			const existingIndex = state.sectionalSummary.allChartSummary.findIndex((item) => item.pageNo === action.payload.pageNo);

			if (existingIndex !== -1) {
				state.sectionalSummary.allChartSummary[existingIndex].loading = action.payload.loading;
				state.sectionalSummary.allChartSummary[existingIndex].isStale = action.payload.isStale;
			} else {
				// Create new entry if doesn't exist
				state.sectionalSummary.allChartSummary.push({
					pageNo: action.payload.pageNo,
					summaryText: "",
					loading: action.payload.loading,
					isStale: action.payload.isStale,
					error: false,
				});
			}
		},
		updateSummaryError(state, action: PayloadAction<{ pageNo: number; error: boolean; isStale: boolean }>) {
			const existingIndex = state.sectionalSummary.allChartSummary.findIndex((item) => item.pageNo === action.payload.pageNo);

			if (existingIndex !== -1) {
				state.sectionalSummary.allChartSummary[existingIndex].error = action.payload.error;
				state.sectionalSummary.allChartSummary[existingIndex].loading = false;
				state.sectionalSummary.allChartSummary[existingIndex].isStale = action.payload.isStale;
			} else {
				// Create new entry if doesn't exist
				state.sectionalSummary.allChartSummary.push({
					pageNo: action.payload.pageNo,
					summaryText: "",
					loading: false,
					error: action.payload.error,
					isStale: action.payload.isStale,
				});
			}
		},
		updateStaleSummaryData(state, action: PayloadAction<{ isStale: boolean }>) {
			// Update isStale key to true for every existing object
			state.sectionalSummary.allChartSummary.forEach((item) => {
				item.isStale = action.payload.isStale;
				item.loading = false;
			});
		},
		updateSingleStaleSummaryData(state, action: PayloadAction<{ pageNo: number; isStale: boolean }>) {
			const existingIndex = state.sectionalSummary.allChartSummary.findIndex((item) => item.pageNo === action.payload.pageNo);

			if (existingIndex !== -1) {
				state.sectionalSummary.allChartSummary[existingIndex].isStale = action.payload.isStale;
			} else {
				// Create new entry if doesn't exist
				state.sectionalSummary.allChartSummary.push({
					pageNo: action.payload.pageNo,
					summaryText: "",
					loading: false,
					error: false,
					isStale: action.payload.isStale,
				});
			}
		},
		resetAllSummaryData(state) {
			state.sectionalSummary.allChartSummary = [];
		},
		setOpenEndedDrawerTabActivekey(state, action: PayloadAction<{ key: number; name: string }>) {
			state.sectionalSummary.openEndedDrawerTabActiveKey = action.payload;
		},
		setOpenEndedDrawer(state, action: PayloadAction<boolean>) {
			state.sectionalSummary.openEndedDrawer = action.payload;
		},
		setCurrentChartIndex(state, action: PayloadAction<number>) {
			state.sectionalSummary.currentChartIndex = action.payload;
		},
		setSectionalDashboardModalOpen(state, action: PayloadAction<boolean>) {
			state.sectionalSummary.sectionalDashboardModalOpen = action.payload;
		},
		setSelectedOEDataForExport(
			state,
			action: PayloadAction<{
				hash: string;
				tabKey: string;
				tabName: string;
				data: { idx: unknown; resp_val: unknown };
				checked: boolean;
			}>
		) {
			const { hash, tabKey, tabName, data, checked } = action.payload;
			const prevHash = state.sectionalSummary.selectedOEDataForExport[hash] || {};
			const prevTab = prevHash[tabKey] || { tabName, data: [] };
			const prevArr = prevTab.data || [];

			if (checked) {
				// Add: push data if not already present (by idx)
				const exists = prevArr.some((item) => item.idx === data.idx);
				const newArr = exists ? prevArr : [...prevArr, data];
				state.sectionalSummary.selectedOEDataForExport = {
					...state.sectionalSummary.selectedOEDataForExport,
					[hash]: {
						...prevHash,
						[tabKey]: {
							tabName,
							data: newArr,
						},
					},
				};
			} else {
				// Remove: filter out by idx
				const newArr = prevArr.filter((item) => item.idx !== data.idx);
				if (newArr.length === 0) {
					// Remove tabKey from hash
					const { [tabKey]: _, ...restTabs } = prevHash;
					if (Object.keys(restTabs).length === 0) {
						// Remove hash if no tabs left
						const { [hash]: __, ...restHashes } = state.sectionalSummary.selectedOEDataForExport;
						state.sectionalSummary.selectedOEDataForExport = restHashes;
					} else {
						state.sectionalSummary.selectedOEDataForExport = {
							...state.sectionalSummary.selectedOEDataForExport,
							[hash]: restTabs,
						};
					}
				} else {
					state.sectionalSummary.selectedOEDataForExport = {
						...state.sectionalSummary.selectedOEDataForExport,
						[hash]: {
							...prevHash,
							[tabKey]: {
								tabName,
								data: newArr,
							},
						},
					};
				}
			}
		},
		updateOEDataInCache: (state, action: PayloadAction<{ cacheKey: string; tabName: string; pageData: any[] }>) => {
			const { cacheKey, tabName, pageData } = action.payload;
			const cacheItem = state.sectionalSummary.allChartsCacheFlat?.[cacheKey];

			if (cacheItem?.data?.[0]?.data) {
				const tabIdx = cacheItem.data[0].data.findIndex((d: any) => (d.name || d.id) === tabName);
				if (tabIdx !== -1) {
					cacheItem.data[0].data[tabIdx].oe_data = pageData;
				}
			}
		},
		resetSelectedOEDataForExport(state) {
			state.sectionalSummary.selectedOEDataForExport = {};
		},
		updateDownloadSelectedTriggered(state, action: PayloadAction<number | null>) {
			if (typeof action.payload === "number") {
				state.sectionalSummary.downloadSelectedTriggered = action.payload;
			} else {
				state.sectionalSummary.downloadSelectedTriggered += 1;
			}
		},
		updateChartCacheForStudy(state, action: PayloadAction<{ study_id: string; active_insights_id: string }>) {
			const { study_id, active_insights_id } = action.payload;
			const now = dayjs().utc();

			Object.entries(state.sectionalSummary.allChartsCacheFlat).forEach(([hashKey, cacheItem]) => {
				// Only process cache items that belong to the target study
				if (cacheItem.studyId !== study_id) {
					return;
				}

				// Check if cache item is still valid (not expired)
				const createdAt = cacheItem?.metadata?.createdAt ? dayjs(cacheItem.metadata.createdAt) : null;
				const isNotExpired = createdAt ? now.diff(createdAt, "day") <= 7 : true;

				if (!isNotExpired) {
					return;
				}

				// Handle activeInsightsId comparison and update
				const currentActiveInsightsId = cacheItem.activeInsightsId;

				if (currentActiveInsightsId === active_insights_id) {
					return;
				} else {
					// Different or missing activeInsightsId - update it
					state.sectionalSummary.allChartsSummaryCacheFlat[hashKey] = {
						...cacheItem,
						activeInsightsId: active_insights_id,
					};
				}
			});
		},
		setIsFilterApplied: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.isFilterApplied = action.payload;
		},
		setIsFilterCleared: (state, action: PayloadAction<boolean>) => {
			state.sectionalSummary.isFilterCleared = action.payload;
		},
		setAllTemplates(state, action: PayloadAction<ITemplate[]>) {
			state.sectionalSummary.allTemplates = action.payload;
		},
		setSelectedTemplate(state, action: PayloadAction<ITemplate>) {
			state.sectionalSummary.selectedTemplate = action.payload;
		},
		addTemplate(state, action: PayloadAction<ITemplate>) {
			state.sectionalSummary.allTemplates.push(action.payload);
			state.sectionalSummary.selectedTemplate = action.payload;
		},
		setExportPPTView(state, action: PayloadAction<ExportPPTViewType>) {
			state.sectionalSummary.exportPPTView = action.payload;
		},
		setIsTemplateUploading(state, action: PayloadAction<boolean>) {
			state.sectionalSummary.isTemplateUploading = action.payload;
		},
		setSearchMode: (state, action) => {
			state.sectionalSummary.searchMode = action.payload;
		},
		setSearchObj: (state, action) => {
			state.sectionalSummary.searchObj = action.payload;
		},
		setChartViewStatus: (state, action: PayloadAction<{ pageNo: string; isChartView: boolean }>) => {
			const { pageNo, isChartView } = action.payload;
			const existingIndex = state.sectionalSummary.chartsViewStatus.findIndex((item) => item.pageNo === pageNo);

			if (existingIndex !== -1) {
				state.sectionalSummary.chartsViewStatus[existingIndex].isChartView = isChartView;
			} else {
				state.sectionalSummary.chartsViewStatus.push({ pageNo, isChartView });
			}
		},
		resetChartViewStatus: (state) => {
			state.sectionalSummary.chartsViewStatus = [];
		},
	},
});

export const {
	setSectionalSummaryAvailable,
	toggleSelectedChart,
	setSectionalSummaryData,
	setLoading,
	setQuantError,
	setErrorMessage,
	setErrorCode,
	setSelectedSegments,
	setSelectedSegments___,
	setAllFiltersData,
	setIsFilterFocused,
	setSelectedFilters,
	setAnalysisiData,
	setMainChart,
	setActiveSegments,
	setMasterDropDown,
	filterSegmentDrawer,
	setSearchText,
	setChartPanelDropDown,
	setChartPanelDropDown2,
	setChartPanelQuestion,
	setFilterArray,
	setAllFilter,
	setFilterSearchText,
	setSelectedAnalysisChart,
	setPaneNo,
	setSegmentMenuSelect,
	setSectionalCategory,
	setStopScrolling,
	setStopScrollingChartPanel,
	setShowChartAnalysis,
	setSectionalChartsData,
	setSelectedItem,
	setChartPanelSelected,
	setMainChartSCrolled,
	setSectionsTotalQuestions,
	setChartPanelChart,
	resetSectionTotalQuestions,
	updateChartTableViewNew,
	setAllLoadedCharts,
	resetAllLoadedCharts,
	setIsCompareActive,
	setChartPanelDropDownSelections,
	resetChartPanelDropDownSelections,
	filterLoadedChartsBySection,
	updateTabsMapping,
	setChartDataPointSelections,
	setOpenDrawer,
	setActiveDrawerTabKey,
	setSectionalChartsAISummary,
	setDisplayState,
	setIsDataUpdateModalOpen,
	setDataUpdateModalMessage,
	updateAllChartsCacheFlat,
	resetAllChartsCacheFlat,
	resetAllChartsCacheBasic,
	filterChartsCacheFlatByStudy,
	updateSelectedChartsForExportHash,
	resetSelectedChartsForExportHash,
	setAllSummaryData,
	updateSummaryLoading,
	updateSummaryError,
	updateSingleStaleSummaryData,
	updateStaleSummaryData,
	resetAllSummaryData,
	setOpenEndedDrawerTabActivekey,
	updateOEDataInCache,
	setOpenEndedDrawer,
	setCurrentChartIndex,
	setSectionalDashboardModalOpen,
	setSelectedOEDataForExport,
	resetSelectedOEDataForExport,
	updateDownloadSelectedTriggered,
	updateAllChartsSummaryCacheFlat,
	resetAllChartsSummaryCacheBasic,
	resetAllChartsSummaryCacheFlat,
	filterChartsSummaryCacheFlatByStudy,
	setSingleSummaryData,
	updateChartCacheForStudy,
	setIsFilterApplied,
	setIsFilterCleared,
	setAllTemplates,
	setSelectedTemplate,
	setExportPPTView,
	setIsTemplateUploading,
	addTemplate,
	setSearchMode,
	setSearchObj,
	setChartViewStatus,
	resetChartViewStatus,
} = sectionalSummarySlice.actions;

const rawSectionalSummaryReducer = sectionalSummarySlice.reducer;

// Stale persisted data from before sectionalSummarySlice was un-whitelisted can
// arrive without the `sectionalSummary` wrapper (old dataFilterTransform saved a
// flat { allChartsCacheFlat, allChartsSummaryCacheFlat }). Re-wrap before any
// reducer runs so every state.sectionalSummary.* access stays safe.
const safeSectionalSummaryReducer: typeof rawSectionalSummaryReducer = (state, action) => {
	if (!state || !(state as ISectionalSummaryState).sectionalSummary) {
		const salvaged = state as Partial<IFilterState> | undefined;
		state = {
			sectionalSummary: {
				...initialState,
				...(salvaged?.allChartsCacheFlat ? { allChartsCacheFlat: salvaged.allChartsCacheFlat } : {}),
				...(salvaged?.allChartsSummaryCacheFlat ? { allChartsSummaryCacheFlat: salvaged.allChartsSummaryCacheFlat } : {}),
			},
		} as ISectionalSummaryState;
	}
	return rawSectionalSummaryReducer(state, action);
};

export default safeSectionalSummaryReducer;
